import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const NotificationContext = createContext(null);

/* ---------------------------------------------------------------
   Storage keys — bump the version suffix to reset everyone's inbox
--------------------------------------------------------------- */
const INBOX_KEY = 'pg.notifications.inbox.v1';
const DISMISSED_KEY = 'pg.notifications.dismissed.v1';
const KNOWN_KEY = 'pg.notifications.known.v1';
const SEEDED_KEY = 'pg.notifications.seeded.v1';
const MUTED_KEY = 'pg.notifications.muted.v1';

const POLL_INTERVAL_MS = 60 * 1000; // 1 minute
const MAX_INBOX = 50;
const MAX_KNOWN = 500;
const TOAST_DURATION_MS = 6500;
const MAX_TOASTS = 3;

/* ---------------------------------------------------------------
   Routes — where each notification type sends the user.
   News and events share the What's Happening page
   (https://intranet.projectgreen.mt/whats-happening) and carry the
   Monday item id in ?item=, which makes the page open that item's modal.
   Vacancies stay on https://intranet.projectgreen.mt/vacancies
--------------------------------------------------------------- */
const WHATS_HAPPENING_ROUTE = '/whats-happening';
const ROUTES = {
  news: WHATS_HAPPENING_ROUTE,
  event: WHATS_HAPPENING_ROUTE,
  vacancy: '/vacancies',
};

function buildLink(type, mondayId) {
  const base = ROUTES[type];
  if (!base) return null;
  if (type === 'vacancy' || mondayId === undefined || mondayId === null) return base;
  return `${base}?item=${encodeURIComponent(String(mondayId))}`;
}

/* Inbox entries saved earlier carry old links ('/news' or the bare
   '/whats-happening'). Rebuild them from the entry id ("news-<id>"). */
function normalizeInbox(entries) {
  if (!Array.isArray(entries)) return [];
  return entries.map((entry) => {
    if (!entry || !ROUTES[entry.type]) return entry;

    const mondayId =
      typeof entry.id === 'string' && entry.id.startsWith('news-')
        ? entry.id.slice('news-'.length)
        : null;

    const link = buildLink(entry.type, mondayId);
    return link && entry.link !== link ? { ...entry, link } : entry;
  });
}

/* ---------------------------------------------------------------
   Monday config
--------------------------------------------------------------- */
const MONDAY_API = 'https://api.monday.com/v2';
const MONDAY_TOKEN = process.env.REACT_APP_MONDAY_TOKEN;
const NEWS_BOARD_ID = process.env.REACT_APP_MONDAY_BOARD_ID;
const VACANCIES_BOARD_ID = process.env.REACT_APP_MONDAY_VACANCIES_BOARD_ID;

const NEWS_COL = 'text_mm8ac6v4';
const EVENT_COL = 'text_mm8acn1z';

/* ---------------------------------------------------------------
   Small helpers
--------------------------------------------------------------- */
function readJSON(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / private mode — ignore */
  }
}

function trimText(value, max = 160) {
  if (!value) return '';
  const clean = String(value).replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function toTimestamp(value) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Date.now() : parsed;
}

/* ---------------------------------------------------------------
   Chime — WebAudio, no asset file needed.
   Browsers block audio until the user has interacted with the page,
   so the context is created/resumed on the first pointer or key event.
--------------------------------------------------------------- */
function createChime() {
  let ctx = null;

  const unlock = () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    if (!ctx) ctx = new AudioCtx();
    if (ctx.state === 'suspended') ctx.resume();
  };

  const play = () => {
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();

    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);

    // Soft two-note corporate chime (A5 -> D6)
    [
      { freq: 880.0, at: 0, dur: 0.42 },
      { freq: 1174.66, at: 0.13, dur: 0.5 },
    ].forEach(({ freq, at, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + at);

      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.22, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + dur);

      osc.connect(gain);
      gain.connect(master);
      osc.start(now + at);
      osc.stop(now + at + dur + 0.05);
    });
  };

  return { unlock, play };
}

/* ---------------------------------------------------------------
   Monday fetch
--------------------------------------------------------------- */
async function fetchBoardItems(boardId) {
  if (!boardId || !MONDAY_TOKEN) return [];

  const query = `
    query ($boardId: [ID!]) {
      boards(ids: $boardId) {
        items_page(limit: 25) {
          items {
            id
            name
            created_at
            column_values { id text }
          }
        }
      }
    }
  `;

  const response = await fetch(MONDAY_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: MONDAY_TOKEN,
      'API-Version': '2023-10',
    },
    body: JSON.stringify({ query, variables: { boardId: [String(boardId)] } }),
  });

  if (!response.ok) throw new Error(`Monday responded ${response.status}`);

  const json = await response.json();
  return json?.data?.boards?.[0]?.items_page?.items || [];
}

/* `ready` = the item has its News/Event text filled in. Rows are usually
   created with just a title and filled in afterwards, so we hold off
   notifying until there is content (otherwise the type is guessed wrong). */
function mapNewsItem(item) {
  const cols = Object.fromEntries(
    (item.column_values || []).map((col) => [col.id, col.text])
  );

  const eventText = (cols[EVENT_COL] || '').trim();
  const newsText = (cols[NEWS_COL] || '').trim();

  // Same rule as fetchNews() in NewsBox.js, so the bell and the News page agree.
  const isEvent = Boolean(eventText) && !newsText;
  const type = isEvent ? 'event' : 'news';

  return {
    id: `news-${item.id}`,
    type,
    title: item.name,
    body: trimText(isEvent ? eventText : newsText),
    link: buildLink(type, item.id),
    timestamp: toTimestamp(item.created_at),
    read: false,
    ready: Boolean(eventText || newsText),
  };
}

function mapVacancyItem(item) {
  return {
    id: `vacancy-${item.id}`,
    type: 'vacancy',
    title: item.name,
    body: 'A new vacancy has been published on the Vacancies page.',
    link: ROUTES.vacancy,
    timestamp: toTimestamp(item.created_at),
    read: false,
    ready: true,
  };
}

/* ---------------------------------------------------------------
   Provider
--------------------------------------------------------------- */
export function NotificationProvider({ children }) {
  const [inbox, setInbox] = useState(() =>
    normalizeInbox(readJSON(INBOX_KEY, []))
  );
  const [toasts, setToasts] = useState([]);
  const [muted, setMuted] = useState(() => readJSON(MUTED_KEY, false));

  const dismissedRef = useRef(new Set(readJSON(DISMISSED_KEY, [])));
  const knownRef = useRef(new Set(readJSON(KNOWN_KEY, [])));
  const chimeRef = useRef(null);
  const mutedRef = useRef(muted);
  const pollingRef = useRef(false);
  const toastTimersRef = useRef(new Map());

  mutedRef.current = muted;

  /* Persist inbox on every change — this is what makes notifications
     survive refreshes and stay put until the user removes them. */
  useEffect(() => {
    writeJSON(INBOX_KEY, inbox.slice(0, MAX_INBOX));
  }, [inbox]);

  useEffect(() => {
    writeJSON(MUTED_KEY, muted);
  }, [muted]);

  /* Audio unlock */
  useEffect(() => {
    chimeRef.current = createChime();

    const handleFirstInteraction = () => chimeRef.current?.unlock();

    window.addEventListener('pointerdown', handleFirstInteraction, {
      once: true,
    });
    window.addEventListener('keydown', handleFirstInteraction, { once: true });

    return () => {
      window.removeEventListener('pointerdown', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };
  }, []);

  /* Clear any pending toast timers on unmount */
  useEffect(() => {
    const timers = toastTimersRef.current;
    return () => {
      timers.forEach((timerId) => window.clearTimeout(timerId));
      timers.clear();
    };
  }, []);

  const persistDismissed = useCallback(() => {
    writeJSON(DISMISSED_KEY, Array.from(dismissedRef.current).slice(-MAX_KNOWN));
  }, []);

  const persistKnown = useCallback(() => {
    writeJSON(KNOWN_KEY, Array.from(knownRef.current).slice(-MAX_KNOWN));
  }, []);

  /* ---------------- Toasts (transient, on-screen) ---------------- */
  const dismissToast = useCallback((id) => {
    const timerId = toastTimersRef.current.get(id);
    if (timerId) {
      window.clearTimeout(timerId);
      toastTimersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const pushToast = useCallback(
    (toast) => {
      if (!toast) return;

      const id =
        toast.id || `toast-${Date.now()}-${Math.random().toString(16).slice(2)}`;

      const entry = {
        type: 'general',
        title: '',
        body: '',
        link: null,
        timestamp: Date.now(),
        ...toast,
        id,
      };

      setToasts((prev) => {
        if (prev.some((existing) => existing.id === id)) return prev;
        return [entry, ...prev].slice(0, MAX_TOASTS);
      });

      const timerId = window.setTimeout(() => {
        toastTimersRef.current.delete(id);
        setToasts((prev) => prev.filter((existing) => existing.id !== id));
      }, toast.duration || TOAST_DURATION_MS);

      toastTimersRef.current.set(id, timerId);
    },
    []
  );

  const clearToasts = useCallback(() => {
    toastTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    toastTimersRef.current.clear();
    setToasts([]);
  }, []);

  /* ---------------- Inbox (persistent) ---------------- */
  const pushNotification = useCallback(
    (notification, { toast = true } = {}) => {
      if (!notification?.id) return;
      if (dismissedRef.current.has(notification.id)) return;

      setInbox((prev) => {
        if (prev.some((entry) => entry.id === notification.id)) return prev;
        return [{ read: false, ...notification }, ...prev].slice(0, MAX_INBOX);
      });

      if (toast) pushToast({ ...notification, id: `toast-${notification.id}` });
    },
    [pushToast]
  );

  /* Poll Monday for newly created News / Events and Vacancies */
  const poll = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;

    try {
      const [newsItems, vacancyItems] = await Promise.all([
        fetchBoardItems(NEWS_BOARD_ID).catch(() => []),
        fetchBoardItems(VACANCIES_BOARD_ID).catch(() => []),
      ]);

      const candidates = [
        ...newsItems.map(mapNewsItem),
        ...vacancyItems.map(mapVacancyItem),
      ].sort((a, b) => b.timestamp - a.timestamp);

      if (!candidates.length) return;

      /* Keep notifications already in the inbox in sync with Monday, so a
         type / title / body that was edited after the first ping (or saved
         wrongly earlier) is corrected. Read state is left untouched. */
      const latestById = new Map(candidates.map((entry) => [entry.id, entry]));

      setInbox((prev) => {
        let changed = false;

        const next = prev.map((entry) => {
          const latest = latestById.get(entry.id);
          if (!latest || !latest.ready) return entry;

          if (
            latest.type !== entry.type ||
            latest.title !== entry.title ||
            latest.body !== entry.body ||
            latest.link !== entry.link
          ) {
            changed = true;
            return {
              ...entry,
              type: latest.type,
              title: latest.title,
              body: latest.body,
              link: latest.link,
            };
          }

          return entry;
        });

        return changed ? next : prev;
      });

      /* Only items that already have their News/Event text count as
         notifiable. Empty rows are skipped and NOT marked as known, so
         they notify once they're filled in. */
      const ready = candidates.filter((entry) => entry.ready);

      const seeded = readJSON(SEEDED_KEY, false);

      /* First ever load on this device: remember what already exists
         so the user doesn't get 25 pings for old items. */
      if (!seeded) {
        ready.forEach((entry) => knownRef.current.add(entry.id));
        persistKnown();
        writeJSON(SEEDED_KEY, true);
        return;
      }

      const fresh = ready.filter(
        (entry) =>
          !knownRef.current.has(entry.id) && !dismissedRef.current.has(entry.id)
      );

      ready.forEach((entry) => knownRef.current.add(entry.id));
      persistKnown();

      if (!fresh.length) return;

      fresh.forEach(({ ready: _ready, ...notification }) =>
        pushNotification(notification)
      );

      if (!mutedRef.current) chimeRef.current?.play();
    } finally {
      pollingRef.current = false;
    }
  }, [persistKnown, pushNotification]);

  useEffect(() => {
    poll();
    const timer = window.setInterval(poll, POLL_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') poll();
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [poll]);

  /* ---------------- Actions ---------------- */
  const markAllRead = useCallback(() => {
    setInbox((prev) => prev.map((entry) => ({ ...entry, read: true })));
  }, []);

  const markOneRead = useCallback((id) => {
    setInbox((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, read: true } : entry))
    );
  }, []);

  const removeOne = useCallback(
    (id) => {
      dismissedRef.current.add(id);
      persistDismissed();
      setInbox((prev) => prev.filter((entry) => entry.id !== id));
    },
    [persistDismissed]
  );

  const clearInbox = useCallback(() => {
    setInbox((prev) => {
      prev.forEach((entry) => dismissedRef.current.add(entry.id));
      persistDismissed();
      return [];
    });
  }, [persistDismissed]);

  const toggleMuted = useCallback(() => {
    setMuted((prev) => !prev);
    chimeRef.current?.unlock();
  }, []);

  const unreadCount = useMemo(
    () => inbox.reduce((total, entry) => (entry.read ? total : total + 1), 0),
    [inbox]
  );

  const value = useMemo(
    () => ({
      // inbox
      inbox,
      unreadCount,
      pushNotification,
      markAllRead,
      markOneRead,
      removeOne,
      clearInbox,
      refresh: poll,

      // toasts (aliases cover the common naming in ToastContainer)
      toasts,
      notifications: toasts,
      pushToast,
      addToast: pushToast,
      notify: pushToast,
      dismissToast,
      removeToast: dismissToast,
      closeToast: dismissToast,
      clearToasts,

      // sound
      muted,
      toggleMuted,
    }),
    [
      inbox,
      unreadCount,
      pushNotification,
      markAllRead,
      markOneRead,
      removeOne,
      clearInbox,
      poll,
      toasts,
      pushToast,
      dismissToast,
      clearToasts,
      muted,
      toggleMuted,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used inside NotificationProvider');
  }
  return ctx;
}

export default NotificationContext;